import { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { faEnvelope, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FetchCampaignInvitesParams, fetchCampaignInvites } from 'api/campaigns';
import { useCampaignMembers } from 'assets/js/pages/campaign-settings/campaign-members/CampaignMembersProvider';
import * as DeletePendingInviteDialog from 'assets/js/pages/campaign-settings/campaign-members/DeletePendingInviteDialog';
import * as InviteReminderDialog from 'assets/js/pages/campaign-settings/campaign-members/InviteReminderDialog';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { dateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignMemberInvite } from 'types/domain';

const columnHelper = createColumnHelper<CampaignMemberInvite>();

const CampaignMembersInviteTable = () => {
    const { data: campaign } = useCurrentCampaign();
    const { search } = useCampaignMembers();
    const campaignId = campaign.id;
    const debouncedSearch = useDebounce(search);
    const { pagination, setPagination } = Table.useReactTableState();

    const [isInvitesPending, invitesParams] = useIsPending<FetchCampaignInvitesParams>(
        {
            page: pagination.pageIndex + 1,
            query: debouncedSearch,
            filter: 'pending',
        },
        500,
    );

    const { data: pendingInvites } = useSuspenseQuery({
        queryKey: campaignQueryKeys.invitesWithFilters(campaignId, invitesParams),
        queryFn: () => fetchCampaignInvites(campaignId, invitesParams),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('email', {
                header: 'Contact',
                cell: info => <span>{info.getValue()}</span>,
                meta: {
                    className: 'w-[350px]',
                },
            }),
            columnHelper.accessor('created_at', {
                header: 'Invite date',
                cell: info => {
                    const date = dateFromServer(info.getValue());
                    return <span>{date.toLocaleString(DateTime.DATE_FULL)}</span>;
                },
                meta: {
                    className: 'h-20',
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const invite = info.row.original;
                    const inviteId = invite.id;

                    return (
                        <div className={'flex w-full items-center justify-end gap-2'}>
                            <InviteReminderDialog.Root invite={invite} />
                            <DeletePendingInviteDialog.Root inviteId={inviteId}>
                                <DeletePendingInviteDialog.Trigger asChild={true}>
                                    <Button
                                        variant="transparent"
                                        size={'sm'}
                                        color={'error'}
                                        shape={'square'}
                                        aria-label="Delete"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </DeletePendingInviteDialog.Trigger>
                            </DeletePendingInviteDialog.Root>
                        </div>
                    );
                },
            }),
        ],
        [],
    );

    const table = useReactTable({
        data: pendingInvites?.data ?? [],
        columns,
        pageCount: pendingInvites?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, pendingInvites);

    return (
        <Table.Root isLoading={isInvitesPending} variant={'bordered'} table={table}>
            <div className={'flex flex-col gap-y-4'}>
                <Box className={'overflow-x-auto'}>
                    <Table.Content>
                        <Table.Header />
                        <Table.Body />
                    </Table.Content>
                    <Table.Empty variant={'primary'}>
                        <Table.EmptyIcon>
                            <FontAwesomeIcon icon={faEnvelope} />
                        </Table.EmptyIcon>
                        <Table.EmptyTitle>No pending invites found</Table.EmptyTitle>
                        <Table.EmptyDescription className="min-w-[350px]">
                            {search
                                ? 'No pending invites match your search criteria.'
                                : 'Invite supporters to fundraise your campaign.'}
                        </Table.EmptyDescription>
                    </Table.Empty>
                </Box>
                <Table.Pagination>
                    <Table.PaginationPageNavigation />
                </Table.Pagination>
            </div>
        </Table.Root>
    );
};

export { CampaignMembersInviteTable };
